/**
 * @module buttercam/utils
 * */

import Console from "./console";
import jump from "./lib/jump";

const _Error = function (errorType, options) {
  let error;
  let Error = global[errorType];
  if(!isFunction(Error)) {
    throw new global.Error(`${errorType} is not defined`);
  }
  if(isString(options)) {
    error = new Error(options)
  } else if(isObject(options)) {
    error = new Error(options.message);
    Object.keys(options).forEach(key => error[key] = options[key]);
  } else {
    error = new Error()
  }
  return error
};

/**
 * 生成伪uuid(随机数)
 * @param {number} [len=32] - 字符串长度
 * @param {number} [radix=16] - 基数
 * @returns {string} 伪uuid
 * */
export function uuid(len = 32, radix = 16) {
  let chars = '0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz'.split('');
  let uuid = [], i;
  radix = radix || chars.length;
  if (len) {
    for (i = 0; i < len; i++) uuid[i] = chars[0 | Math.random()*radix];
  } else {
    let r;
    
    uuid[8] = uuid[13] = uuid[18] = uuid[23] = '-';
    uuid[14] = '4';
    
    for (i = 0; i < 36; i++) {
      if (!uuid[i]) {
        r = 0 | Math.random()*16;
        uuid[i] = chars[(i === 19) ? (r & 0x3) | 0x8 : r];
      }
    }
  }
  return uuid.join('');
}

/**
 * 过滤对象，默认为去空值
 * @param {object} object - 过滤目标
 * @param {function} [filter=value => typeof value === 'number' || !!value] - 过滤器，返回true则通过
 * @returns {object} 过滤后的对象
 * */
export function filterObject(object, filter) {
  if(typeof filter !== 'function') {
    filter = value => typeof value === 'number' || !!value;
  }
  return Object.keys(object).reduce((accumulator, key) => {
    if(filter(object[key])) {
      accumulator[key] = object[key];
    }
    return accumulator
  }, {})
}

/**
 * 获取运行环境
 * @returns {object} 环境量
 * */
export function getGlobal() {
  if(typeof window === 'undefined') {
    return {}
  }
  if(process.env.NODE_ENV === 'test') {
    return window.TEST_ENV || window
  }
  return window
}

/**
 * 全局对象
 * @type {Object}
 * */
export const global = getGlobal();

/**
 * @description 通过字串判断是否为ios设备
 * @param {string} str - 类似userAgent的字符串
 * @returns {boolean} - 是否为ios
 * */
export function isIOS(str) {
  if(!isString(str)) {
    return false
  }
  return /(ios|iphone|ipad|ipod|itouch|iwatch)/.test(str.toLowerCase())
}

/**
 * @description 判断是否为iphoneX
 * @param {string} str - 类似userAgent的字符串
 * @returns {boolean} - 是否为iphoneX
 * */
export function isIPhoneX(str) {
  const global = getGlobal();
  const ratio = global.devicePixelRatio || 1;
  if(!isIOS(str)) return false;
  return global.screen.width * ratio === 1125 && global.screen.height * ratio === 2436
}

/**
 * @description 获取手机操作系统
 * @returns {string} - 操作设备
 * */
export function getMobileOperatingSystem() {
  const global = getGlobal();
  const userAgent = (navigator.userAgent || navigator.vendor || global.opera).toLowerCase();
  if (isIOS(userAgent)) {
    return 'ios';
  }
  if (/android/.test(userAgent)) {
    return 'android';
  }
  if (/(windows phone|iemobile|wpdesktop)/.test(userAgent)) {
    return 'windows'
  }
  return 'pc';
}

/**
 * @description 比较两个版本的大小
 * @param {string} a - 待比较的版本号
 * @param {string} b - 待比较的版本号
 * @returns {number} 0 -> a = b; 1 -> a > b; -1 a < b
 * @example
 * versionCompare('5.5.3', '5.5.2') // => 1
 * versionCompare('5.5.5', '5.5.5') // => 0
 * versionCompare('5.4.5', '5.5.0') // => -1
 * */
export function versionCompare(a, b) {
  if (!a) return -1;
  
  if (a === b) {
    return 0;
  }
  
  let a_components = a.split(".");
  let b_components = b.split(".");
  
  let len = Math.min(a_components.length, b_components.length);
  
  for (let i = 0; i < len; i++) {
    if (parseInt(a_components[i]) > parseInt(b_components[i])) {
      return 1;
    }
    if (parseInt(a_components[i]) < parseInt(b_components[i])) {
      return -1;
    }
  }
  
  if (a_components.length > b_components.length) {
    return 1;
  }
  
  if (a_components.length < b_components.length) {
    return -1;
  }
  
  return 0;
}

/**
 * 值是否为数字
 * @param {*} value - 被判断的值
 * @returns {boolean}
 * */
export function isNumber(value) {
  return typeof value === 'number' && !isNaN(value)
}

/**
 * 值是否为字符串
 * @param {*} value - 被判断的值
 * @returns {boolean}
 * */
export function isString(value) {
  return typeof value === 'string'
}

/**
 * 值是否为数组
 * @param {*} value - 被判断的值
 * @returns {boolean}
 * */
export function isArray(value) {
  return Array.isArray(value)
}

/**
 * 值是否为布尔值
 * @param {*} value - 被判断的值
 * @returns {boolean}
 * */
export function isBoolean(value) {
  return typeof value === 'boolean'
}

/**
 * 值是否为对象
 * @param {*} value - 被判断的值
 * @returns {boolean}
 * */
export function isObject(value) {
  return !!value && typeof value === 'object' && !isArray(value)
}

/**
 * 值是否为函数
 * @param {*} value - 被判断的值
 * @returns {boolean}
 * */
export function isFunction(value) {
  return typeof value === 'function'
}

/**
 * 值是否为 undefined
 * @param {*} [value] - 被判断的值
 * @returns {boolean}
 * */
export function isUndefined(value) {
  return typeof value === 'undefined'
}

/**
 * 获取 Console 实例
 * @param {string} [scope="Butter Debug"] - 标签
 * @returns {Console} {@link module:buttercam/console~Console}
 * */
export function Debug(scope = 'Butter Debug') {
  return new Console(scope)
}

/**
 * 字符串 转为 8位无符号整数(0~255)的数组 (只支持编号0~255的字符)
 * @param {String} string - 需要转换的字符串
 * @returns {Uint8Array}
 * */
export function stringToUnit8Array(string) {
  let arr=[];
  for(let i=0;i<string.length;++i){
    arr.push(string.charCodeAt(i));
  }
  return new Uint8Array(arr);
}

/**
 * 字符串 转为 8位无符号整数(0~65535)的数组 (只支持编号0~65535的字符)
 * @param {String} string - 需要转换的字符串
 * @returns {Uint16Array}
 * */
export function stringToUnit16Array(string) {
  let arr=[];
  for(let i=0;i<string.length;++i){
    arr.push(string.charCodeAt(i));
  }
  return new Uint16Array(arr);
}

/**
 * 字符串 转为 8位无符号整数(0~4294967295)的数组 (只支持编号0~4294967295的字符)
 * @param {String} string - 需要转换的字符串
 * @returns {Uint32Array}
 * */
export function stringToUnit32Array(string) {
  let arr=[];
  for(let i=0;i<string.length;++i){
    arr.push(string.charCodeAt(i));
  }
  return new Uint32Array(arr);
}

/**
 * 把 unicode 转换为 字符串
 * @param {Number|Array.<Number>|Uint8Array|Uint16Array|Uint32Array} codes - unicode
 * @returns {String}
 * @example
 * let uint32 = stringToUnit32Array('测试一下');
 *
 * getStringFromUnicodes(uint32); // => '测试一下'
 * getStringFromUnicodes(65); // => 'A'
 * getStringFromUnicodes([65, 66, 67]); // => 'ABC'
 * */
export function getStringFromUnicodes(codes) {
  if(isNumber(codes)) {
    return String.fromCharCode(codes)
  }
  return String.fromCharCode(...codes)
}

/**
 * base64编码
 * @param {String} byteString - Latin1 范围内
 * @returns {String}
 * */
export function base64Encode(byteString) {
  return window.btoa(byteString)
}

/**
 * base64解码
 * @param {String} base64 - base64码
 * @returns {String}
 * */
export function base64Decode(base64) {
  return window.atob(base64)
}

/**
 * 尝试转换 JSON 字符串
 * @param {String|Object} payload - 尝试转换的对象
 * @returns {Object}
 * @throws {TypeError} will throw an error if payload is not Object or String
 * @throws {Error} will throw an error if JSON.parse failed
 * */
export function parseJSON(payload) {
  if(isObject(payload) || isArray(payload)) {
    return payload
  }
  if(!isString(payload)) {
    throw new TypeError('payload is not a String')
  }
  try {
    return JSON.parse(payload)
  } catch (e) {
    throw new Error(`"${payload}" is not a valid JSON String`)
  }
}

/**
 * Error
 * @param {Object|string} params - 错误信息
 * @return {Error}
 * @example
 * import {Error} from "buttercam/utils";
 *
 * //参数为 String
 * const error = new Error('catch an error');  // => Error Instance
 *
 * error.message = 'catch an error';
 *
 * //参数为 Object
 * const error = new Error({
 *   message: 'catch an error',
 *   userMessage: '捕获到一个错误'
 * }); // => Error Instance
 *
 * error.message = 'catch an error';
 * error.userMessage = '捕获到一个错误';
 * */
export function Error(params) {
  return _Error('Error', params)
}

/**
 * TypeError
 * @param {Object|String} params - 错误信息
 * @return {TypeError}
 * */
export function TypeError(params) {
  return _Error('TypeError', params)
}

/**
 * URIError
 * @param {Object|String} params - 错误信息
 * @return {URIError}
 * */
export function URIError(params) {
  return _Error('URIError', params)
}

/**
 * EvalError
 * @param {Object|String} params - 错误信息
 * @return {EvalError}
 * */
export function EvalError(params) {
  return _Error('EvalError', params)
}

/**
 * InternalError
 * @param {Object|String} params - 错误信息
 * @return {InternalError}
 * */
export function InternalError(params) {
  return _Error('InternalError', params)
}

/**
 * RangeError
 * @param {Object|String} params - 错误信息
 * @return {RangeError}
 * */
export function RangeError(params) {
  return _Error('RangeError', params)
}

/**
 * ReferenceError
 * @param {Object|String} params - 错误信息
 * @return {ReferenceError}
 * */
export function ReferenceError(params) {
  return _Error('ReferenceError', params)
}

/**
 * SyntaxError
 * @param {Object|String} params - 错误信息
 * @return {SyntaxError}
 * */
export function SyntaxError(params) {
  return _Error('SyntaxError', params)
}

/**
 * 转换色值(#ff5acbd3 带 alpha 通道的色值转换成 rgba, 普通色值不变)
 * @param {String} color - 色值
 * @return {String}
 * */
export function parseColor(color) {
  if(!isString(color)) {
    throw new TypeError('[buttercam~parseColor] color must be String')
  }
  if(!/^#/.test(color)) {
    throw new TypeError('[buttercam~parseColor] color is invalid')
  }
  if(color.length <= 7) {
    return color
  }
  if(color.length === 9) {
    try {
      const alpha = (parseInt(color.slice(1, 3), 16) / 255).toFixed(2);
      const r = parseInt(color.slice(3, 5), 16);
      const g = parseInt(color.slice(5, 7), 16);
      const b = parseInt(color.slice(7), 16);
      return `rgba(${r},${g},${b},${alpha})`
    } catch (e) {
      return color
    }
  }
  return color
}

/**
 * 滑动到目标位置
 * @param {Object|String} target - 目标，可以是 css selector 或是 dom object
 * @param {Object} [options] - 选项
 * @param {Number} [options.duration] - 时长
 * @param {Number} [options.offset] - 偏移
 * @param {Function} [options.callback] - 回调，活动结束后触发
 * @param {Function} [options.easing] - 过渡函数
 * @return void
 * */
export function scrollTo(target, options) {
  jump.to(target, options)
}

/**
 * 获取执行器
 * @param {Function} fun - 要被执行的函数
 * @param {Object} context - 执行环境
 * @return {*} 优先返回函数返回值，否则返回 undefined
 * */
export function getExecutor(fun, context) {
  return function (...args) {
    try {
      return fun.apply(context || this, args)
    } catch (e) {
      return undefined
    }
  }
}

/**
 * 通过名称获取执行器
 * @param {string} name - 名称
 * @param {Object} namespace=window - 命名空间
 * @return {*}
 * */
export function getExecutorByName(name, namespace = global) {
  try {
    return getExecutor(namespace[name], namespace)
  } catch (e) {
    return () => undefined
  }
}

/**
 * 发送 scheme
 * @function
 * @param {String} scheme - scheme
 * @return {Promise}
 * */
export async function sendScheme(scheme) {
  const document = global.document;
  const iframe = document.createElement('iframe');
  iframe.style.width = '0';
  iframe.style.height = '0';
  iframe.style.border = '0';
  iframe.style.padding = '0';
  iframe.style.margin = '0';
  iframe.style.opacity = '0';
  iframe.src = scheme;
  document.body.appendChild(iframe);
  return new Promise((resolve) => {
    setTimeout(() => {
      document.body.removeChild(iframe);
      resolve()
    }, 10)
  })
}

/**
 * 暴力克隆(函数无法克隆)
 * @param {Object|Array} object - 克隆对象
 * @returns {Object}
 * */
export function forceClone(object) {
  try {
    return JSON.parse(JSON.stringify(object))
  } catch (e) {
    return object
  }
}

/**
 * 内存中加载图片
 * @param {String} src - 图片地址
 * @param {Object} attrs - img 标签附加属性
 * @returns {Promise}
 * */
export function loadImage(src, attrs = {}) {
  return new Promise(function (resolve, reject) {
    const image = new Image();
    
    image.onload = function () {
      resolve(image)
    };
    
    image.onerror = function (err) {
      reject(new Error({src, message: err.message}))
    };
    
    Object.keys(attrs).forEach((key) => {
      image.setAttribute(key, attrs[key])
    });
    
    image.src = src
  })
}

/**
 * 等待
 * @param {Number} time - 延迟时间
 * @returns {Promise}
 * @example
 * async function() {
 *   await sleep(100); //(异步)等待 100ms
 *   //do something...
 * }
 * */
export function sleep(time) {
  return new Promise(resolve => {
    setTimeout(resolve, time)
  })
}